import React from "react";
import {
    Alert,
    Box,
    Checkbox,
    FormControlLabel,
    Grid,
    IconButton,
    InputAdornment,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material";
import { ContentCopy } from "@mui/icons-material";

import { CustomeTextField } from "../core/CustomeTextField";
import { IServiceToken, SERVICE_SCOPES } from "../../interfaces/IServiceToken";

export interface IProps {
    currentToken: IServiceToken;
    setCurrentToken: Function;
}

export const getEmptyServiceToken = () => {
    return {
        id: -1,
        name: "",
        token: "",
        scopes: "extension,release",
        can_write: true,
        is_active: true,
        note: "",
        created_at: "",
        last_used_at: null,
    } as IServiceToken;
};

export const ServiceTokenEdit: React.FC<IProps> = ({ currentToken, setCurrentToken }) => {
    const scopeList = (currentToken.scopes || "")
        .split(",")
        .map(x => x.trim())
        .filter(x => x !== "");

    const toggleScope = (scope: string, checked: boolean) => {
        const next = checked
            ? Array.from(new Set([...scopeList, scope]))
            : scopeList.filter(x => x !== scope);
        setCurrentToken({ ...currentToken, scopes: next.join(",") });
    };

    const handleCopy = () => {
        navigator.clipboard.writeText(currentToken.token);
    };

    return (
        <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
                <Alert severity="info">
                    Ein Service-Token erreicht ausschließlich die Erweiterungen und die
                    Changelogs. Kunden-, Vertrags- und Abrechnungsdaten sowie die
                    Freigabe eines Changelogs bleiben ihm verwehrt.
                </Alert>
            </Grid>

            <Grid item xs={12} sm={8}>
                <CustomeTextField
                    label="Bezeichnung"
                    attr="name"
                    type="string"
                    object={currentToken}
                    setObject={setCurrentToken}
                    required
                />
            </Grid>
            <Grid item xs={12} sm={4}>
                <CustomeTextField
                    label="Aktiv"
                    attr="is_active"
                    type="boolean"
                    object={currentToken}
                    setObject={setCurrentToken}
                    required
                />
            </Grid>

            <Grid item xs={12}>
                <Typography variant="subtitle2">Berechtigungen</Typography>
                <Box>
                    {SERVICE_SCOPES.map(scope => (
                        <FormControlLabel
                            key={`scope-${scope.value}`}
                            control={
                                <Checkbox
                                    checked={scopeList.includes(scope.value)}
                                    onChange={(_, checked) => toggleScope(scope.value, checked)}
                                />
                            }
                            label={scope.title}
                        />
                    ))}
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={currentToken.can_write}
                                onChange={(_, checked) =>
                                    setCurrentToken({ ...currentToken, can_write: checked })
                                }
                            />
                        }
                        label="Darf ändern (sonst nur lesen)"
                    />
                </Box>
            </Grid>

            <Grid item xs={12}>
                <CustomeTextField
                    label="Notiz"
                    attr="note"
                    type="string"
                    object={currentToken}
                    setObject={setCurrentToken}
                    rows={3}
                />
            </Grid>

            {currentToken.id > 0 && (
                <Grid item xs={12}>
                    <TextField
                        label="Token"
                        value={currentToken.token}
                        fullWidth
                        InputProps={{
                            readOnly: true,
                            endAdornment: (
                                <InputAdornment position="end">
                                    <Tooltip title="In die Zwischenablage kopieren">
                                        <IconButton onClick={handleCopy}>
                                            <ContentCopy />
                                        </IconButton>
                                    </Tooltip>
                                </InputAdornment>
                            ),
                        }}
                        helperText="Wird im Header X-Service-Token übergeben."
                    />
                </Grid>
            )}

            {currentToken.id < 0 && (
                <Grid item xs={12}>
                    <Alert severity="warning">
                        Das Token wird beim Speichern erzeugt und ist danach hier
                        einsehbar.
                    </Alert>
                </Grid>
            )}
        </Grid>
    );
};
